import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SectionHeader } from '../components/ui/SectionHeader';
import { ArrowRight, Sparkles, Play, Disc } from 'lucide-react';
import { SEO } from '../components/SEO';
import { getReleasesForClient } from '../services/releaseService';
import { getArtistsForClient } from '../services/artistService';
import { Release, Artist } from '../types';

export const Home: React.FC = () => {
  const [latestReleases, setLatestReleases] = useState<Release[]>([]);
  const [featuredArtist, setFeaturedArtist] = useState<Artist | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const releases = await getReleasesForClient();
      setLatestReleases(releases.slice(0, 3)); // Only take top 3 latest
      
      const artists = await getArtistsForClient();
      const flex = artists.find(a => a.name.toLowerCase() === 'fl3x');
      if (flex) {
          setFeaturedArtist(flex);
      }
    };
    fetchData();
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "additionalType": "https://schema.org/RecordLabel",
    "name": "AstralVibe",
    "alternateName": "AstralVibe Records",
    "url": "https://astralvibe.art",
    "logo": "https://astralvibe.art/og-image.png",
    "image": "https://astralvibe.art/og-image.png",
    "description": "AstralVibe is a premier electronic music record label and artist management agency specializing in Afro House, Techno, and cutting-edge electronic music.",
    "sameAs": [
      "https://www.instagram.com/astralvibemusic",
      "https://open.spotify.com/user/astralvibe"
    ]
  };

  return (
    <div className="w-full overflow-x-hidden">
      <SEO 
        title="AstralVibe | Electronic Music Record Label" 
        description="AstralVibe is a premier electronic music record label and artist management agency. Explore the void where house, techno, and futuristic synthesis meet."
        keywords={["electronic music label", "record label", "etichetta discografica", "techno label", "afro house label", "artist management", "music agency", "AstralVibe", "underground music"]}
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20">
        {/* Glow Effects - Dimmed */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vh] bg-astral-purple rounded-full blur-[150px] opacity-10 animate-pulse-slow pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-[40vw] h-[40vh] bg-astral-cyan rounded-full blur-[150px] opacity-5 pointer-events-none"></div>
        
        <div className="relative z-10 container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-astral-purple/30 rounded-full bg-astral-purple/5 backdrop-blur-md mb-8 animate-in fade-in slide-in-from-top-4 duration-1000">
             <Sparkles className="w-3 h-3 text-astral-purple" />
             <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">Record Label & Agency</span>
          </div>
          
          <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl leading-tight md:leading-none font-display font-black text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-gray-500 tracking-tighter mb-8 animate-in fade-in zoom-in duration-1000 text-glow-strong">
            ASTRALVIBE
          </h1>
          
          <p className="text-slate-300 text-base md:text-xl font-light tracking-[0.2em] uppercase max-w-3xl mx-auto mb-16 animate-in slide-in-from-bottom-5 duration-1000 delay-300">
            Where sound meets the infinite. <br/>
            <span className="text-astral-purple font-bold">House & Techno</span> in its most celestial form.
          </p>
          
          <div className="flex flex-col md:flex-row gap-6 justify-center animate-in slide-in-from-bottom-5 duration-1000 delay-500">
            <Link 
              to="/releases"
              className="group block text-center px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-display font-bold uppercase tracking-widest hover:bg-astral-purple hover:border-astral-purple transition-all duration-300 shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_30px_rgba(96,37,192,0.4)] hover:scale-105"
            >
              Enter The Archive
            </Link>
            <Link 
              to="/artists"
              className="block text-center px-8 py-4 border border-white/10 text-gray-300 font-display font-bold uppercase tracking-widest hover:border-astral-purple hover:text-white hover:bg-astral-purple/5 transition-all duration-300 backdrop-blur-sm"
            >
              Meet The Architects
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Releases Section */}
      <section className="py-24 relative overflow-hidden bg-black border-y border-white/5">
         <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16">
                <div>
                    <span className="text-astral-purple font-mono uppercase tracking-[0.2em] text-xs font-bold block mb-4">New Transmissions</span>
                    <h2 className="text-4xl md:text-5xl font-display font-black text-white uppercase tracking-tighter">
                       Latest Releases
                    </h2>
                </div>
                <Link to="/releases" className="group flex items-center gap-3 text-gray-400 hover:text-white transition-colors mt-6 md:mt-0 uppercase tracking-widest text-xs font-bold border-b border-white/20 pb-1 hover:border-astral-purple">
                    View Full Catalog <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform text-astral-purple" />
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
               {latestReleases.map(release => (
                   <Link key={release.id} to={`/releases/${release.id}`} className="group block relative">
                       <div className="relative aspect-square overflow-hidden bg-astral-black border border-white/10 group-hover:border-astral-purple/50 transition-colors duration-500 mb-6">
                           <img 
                               src={release.cover} 
                               alt={release.title} 
                               className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 group-hover:brightness-50"
                           />
                           <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                               <div className="w-16 h-16 rounded-full border border-white/30 flex items-center justify-center text-white backdrop-blur-md group-hover:scale-110 transition-transform duration-300">
                                   <Play className="w-6 h-6 ml-1 fill-white" />
                               </div>
                           </div>
                           <div className="absolute top-4 right-4 px-3 py-1 bg-black/60 backdrop-blur-md border border-white/20 text-[10px] font-mono text-white uppercase tracking-widest rounded-sm">
                               {release.catalogNumber}
                           </div>
                       </div>
                       <div>
                           <h3 className="text-2xl font-display font-bold uppercase text-white group-hover:text-astral-purple transition-colors">{release.title}</h3>
                           <p className="text-slate-400 text-sm tracking-widest uppercase mt-2">{release.artist}</p>
                       </div>
                   </Link>
               ))}
               
               {latestReleases.length === 0 && (
                   <div className="col-span-3 py-20 text-center border border-white/10 rounded-sm bg-white/5">
                       <Disc className="w-8 h-8 text-white/20 mx-auto mb-4" />
                       <p className="text-white/50 font-mono text-xs uppercase tracking-widest">No signals detected</p>
                   </div>
               )}
            </div>
         </div>
      </section>

      {/* Featured Artists (Static Preview) */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-6">
           <SectionHeader subtitle="The Roster" title="Featured Architects" />
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to={featuredArtist ? `/artists/${featuredArtist.id}` : "/artists"} className="group relative aspect-video overflow-hidden border border-white/10">
                 <img src={featuredArtist?.image || "https://i.scdn.co/image/ab6761610000e5eb1b95c9939a6897781d500a8c"} alt="FL3X" className="w-full h-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105" />
                 <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"></div>
                 <div className="absolute bottom-8 left-8">
                    <h3 className="text-4xl font-display font-black text-white uppercase tracking-tighter mb-2">FL3X</h3>
                    <p className="text-astral-purple font-mono text-xs uppercase tracking-[0.2em]">{featuredArtist?.genre || "Afro House / Tech House"}</p>
                 </div>
              </Link>
              <Link to="/demo" className="group relative aspect-video overflow-hidden border border-white/10">
                 <img src="/join-roster.jpg" alt="Join The Roster" className="w-full h-full object-cover grayscale brightness-50 transition-all duration-700 group-hover:brightness-100 group-hover:scale-105" />
                 <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                 <div className="absolute bottom-8 left-8">
                    <h3 className="text-4xl font-display font-black text-white uppercase tracking-tighter mb-2 text-glow">Join The Roster</h3>
                    <p className="text-slate-300 font-mono text-xs uppercase tracking-[0.2em]">Submit Your Demo</p>
                 </div>
                 <div className="absolute top-8 right-8">
                     <ArrowRight className="w-8 h-8 text-white opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                 </div>
              </Link>
           </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-24 relative overflow-hidden bg-white/5 border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-display font-black text-white uppercase tracking-tighter mb-8">
               Symphonies of the Cosmos
            </h2>
            <p className="text-slate-300 font-light leading-relaxed mb-8 text-lg sm:text-xl">
              We don't just release music; we curate sonic expeditions. <span className="text-astral-purple font-bold">AstralVibe</span> exists at the intersection of raw rhythm and celestial melody. Born from the silence between the stars, our label is a sanctuary for producers exploring the outer limits.
            </p>
            <div className="flex justify-center mt-12">
              <Link 
                to="/services"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-astral-purple/20 border border-astral-purple text-white uppercase tracking-[0.2em] text-sm font-bold hover:bg-astral-purple hover:scale-105 transition-all duration-300"
              >
                Explore Services & Label Ops
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};